import React from "react";
import Image from "next/image";
import { Checkbox } from "@mui/material";
import { Box } from "@mui/system";
import moment from "moment";
import { useCityName } from "@/utils/customHooks/useCityName";
import { PassengerSegment, Journey } from "@/types/*";
import { CHECKIN_OPENS_48_HOURS_PRIOR } from "../../../constants/variables";
import getNoOfStops from "../../../helper/flightStops";
import { RightArrow } from "../../../public/svg/RightArrow";
import cssStyle from "./checkIn.module.css";
import { CheckInStatusForCard } from "./helperFunctions/CheckedInStatusCard";
import { getJourneySegmentIdentifiers } from "./helperFunctions/getJourneySegmentIdentifiers";
import { extractResource } from "helper/extractResource";

interface CheckinJourneySelectionCardProps {
  journeyDetailsInfo: { journeyDetails: Journey; isChecked: boolean };
  passengerSegment: PassengerSegment;
  resourceData: Record<string, string>;
  isChangeBooking: boolean;
  journeys: Journey[];
  toggle: () => void;
  id: number;
  isPreviousFlight: boolean;
}

export function CheckinJourneySelectionCard({
  journeyDetailsInfo,
  resourceData,
  isChangeBooking,
  journeys,
  toggle,
  id,
  isPreviousFlight
}: CheckinJourneySelectionCardProps) {
  const AccessTime = "/svg/time.svg";
  const lab = { inputProps: { "aria-label": "Checkbox demo" } };
  const cityName = useCityName();
  const checkedInCount = journeyDetailsInfo.journeyDetails.segments[0].passengerSegment.filter(
    (data: PassengerSegment) => data?.liftStatus === "CheckedIn" || data?.liftStatus === "Boarded"
  ).length;
  const totalPassengerCount = journeyDetailsInfo.journeyDetails.segments[0].passengerSegment.length;
  const newRestriction = journeyDetailsInfo?.data?.checkinByJourneyRequirements?.restrictions;
  const displayJourneyCards = () => {
    if (journeyDetailsInfo.journeyDetails?.segments) {
      const { journeyDetails } = journeyDetailsInfo;
      return (
        <div id="flightDetails" className="w-full flex justify-between items-center pt-2 pb-2" key={journeyDetails.journeyKey}>
          <div className="flex bg-background-primary w-full">
            <div id="flightDate" className={`${cssStyle.AutoHeight} w-12 h-12 bg-gray-200 rounded`}>
              <Box className={cssStyle.DateMonth}>
                <p id="journeyDay" className="m-0 mb-1 font-semibold text-2xl leading-6 text-center uppercase text-gray-800">
                  {moment(journeyDetails.designator.departure).format(" D")}
                </p>
                <p id="journeyMonth" className="m-0 mb-1 font-semibold text-xs leading-3 text-center uppercase text-gray-800">
                  {moment(journeyDetails.designator.departure).format(" MMM")}
                </p>
              </Box>
            </div>
            <Box id="flightJourney" className={cssStyle.widthFifty}>
              <div className="flex flex-row mt-1 items-center">
                <p id="journeyOrigin" className="font-semibold text-sm m-0 leading-3 flex items-center ml-2 text-typography-primary">
                  {cityName(journeyDetails.designator.origin)}
                </p>
                <div className="ml-2">
                  <RightArrow />
                </div>
                <p id="journeyDestination" className="font-semibold text-sm m-0 leading-3 flex items-center ml-2 text-typography-primary">
                  {cityName(journeyDetails.designator.destination)}
                </p>
              </div>
              <p id="journeyDetails" className="m-0 font-normal text-xs flex items-center pt-2 ml-2 text-typography-secondary">
                {getNoOfStops(journeyDetails.segments.length - 1, journeyDetails.flightType)}
                &nbsp;•&nbsp;
                {getJourneySegmentIdentifiers(journeyDetails)}
              </p>

              {CheckInStatusForCard({ checkedInCount, totalPassengerCount, resourceData })}

              {newRestriction?.includes("NotOpened") ? (
                <Box>
                  <div className="flex flex-row items-center font-medium text-xs pl-2 pt-2 pb-1 text-gray-700">
                    <Image src={AccessTime} alt="Access Time" width={12} height={12} />
                    {CHECKIN_OPENS_48_HOURS_PRIOR}
                  </div>
                </Box>
              ) : (
                <Box>
                  <div className="flex flex-row items-center font-medium text-xs pl-2 pt-2 pb-1 text-gray-700">
                    {newRestriction?.includes("Closed") ? extractResource(resourceData,"flown") :  null }  
                  </div>
                </Box>
              )}
            </Box>
          </div>
          {(isChangeBooking && !journeyDetailsInfo?.journeyDetails?.wasAnyCheckInDone) ||
          (!isChangeBooking &&
            !newRestriction?.includes("NotOpened") &&
            journeys.length > 0 &&
            !newRestriction?.includes("Closed") &&
            journeyDetailsInfo.journeyDetails.segments[0].passengerSegment.filter(
              (data: PassengerSegment) => data.liftStatus === "CheckedIn" || data.liftStatus === "Boarded"
            ).length !== journeyDetailsInfo.journeyDetails.segments[0].passengerSegment.length) ? (
            <div className="bg-background-primary px-2 sm:px-0 dark:py-[25.5px] sm:dark:py-[17px] min-h-full rounded-r">
              <Checkbox
                id="checkbox"
                checked={journeyDetailsInfo?.isChecked || false}
                name={`${id}`}
                onChange={toggle}
                color="primary"
                {...lab}
                disabled={isPreviousFlight}
              />
            </div>
          ) : null}
        </div>
      );
    }
    return null;
  };

  return <div className="w-full ">{displayJourneyCards()}</div>;
}
